import {NoEntryFillIcon, TerminalIcon} from '@primer/octicons-react'
import {ResolvedPromptDisplay} from './ResolvedPromptDisplay'
import styles from './ResolvedPermission.module.css'

export interface ResolvedPermissionProps {
  /** The kind of permission: 'commands', 'shell', 'write', 'mcp', or a tool name */
  kind: string
  /** The tool requesting permission */
  toolName?: string
  /** Name of the MCP server providing the tool (for kind: "mcp") */
  serverName?: string
  /** What the tool intended to do — shown as the header title */
  description?: string
  /** Command text or filename */
  detail?: string
  /** The denial result kind from the runtime */
  denialKind?: string
}

/** Map denial kind to a human-friendly footer message */
function getDenialMessage(denialKind?: string): string {
  switch (denialKind) {
    case 'denied-by-rules':
      return 'Blocked by repository policy'
    case 'denied-by-content-exclusion-policy':
      return 'Blocked by content exclusion policy'
    case 'denied-no-approval-rule-and-could-not-request-from-user':
      return 'No approval rule configured'
    case 'aborted-by-user':
      return 'Aborted by user'
    case 'denied-interactively-by-user':
    default:
      return 'The user rejected this tool call'
  }
}

/** Resolved permission prompt shown in the session timeline — only for denied permissions */
export function ResolvedPermission({
  kind,
  toolName,
  serverName,
  description,
  detail,
  denialKind,
}: ResolvedPermissionProps) {
  const isCommand = kind === 'commands' || kind === 'shell'
  const isWrite = kind === 'write'
  const isMcp = kind === 'mcp'
  const fallbackTitle = isCommand
    ? 'Run command'
    : isWrite
      ? 'Edit file'
      : isMcp
        ? `MCP tool: ${serverName ? `${serverName}/` : ''}${toolName ?? 'unknown'}`
        : `Permission: ${toolName ?? kind}`
  const title = description || fallbackTitle

  const rejectionFooter = (
    <div className={styles.deniedFooter}>
      <NoEntryFillIcon size={16} className={styles.deniedIcon} />
      <span className={styles.deniedText}>{getDenialMessage(denialKind)}</span>
    </div>
  )

  return (
    <ResolvedPromptDisplay icon={<TerminalIcon size={16} />} title={title} footer={rejectionFooter}>
      {detail}
    </ResolvedPromptDisplay>
  )
}
